import { readPref, savePref } from '../../lib/prefs';

// What this device can do for screen sharing, and the share settings, without loading the
// LiveKit client: pages outside rooms (Settings, the speed test) import from here, so the
// ~400 KB relay library only loads with a room.

export type ShareMode = 'detail' | 'motion';

/**
 * adaptive: several sizes (simulcast) or SVC layers, so each viewer gets what their own link and
 *   screen can carry. A viewer on a slow link sees a smaller picture instead of stalls.
 * 4k: one full-resolution layer that never steps down. Every viewer gets the sharer's full
 *   resolution (up to 3840×2160) or stutters trying; nobody is ever sent a smaller version.
 */
export type ShareQuality = 'adaptive' | '4k';

/**
 * Whether this browser can capture a screen at all. Phone and tablet browsers (Chrome on Android,
 * Safari on iPhone/iPad) have no getDisplayMedia, so they can watch shares but not start one.
 */
/** window.VBNative: present only inside the Video Bridge Android app (apps/android). */
interface NativeBridge {
  isScreenShareSupported(): boolean;
  startScreenShare(json: string): void;
  stopScreenShare(): void;
  isSharing(): boolean;
  version(): string;
  // Push notifications (app 1.3.0+)
  pushConfigured?(): boolean;
  pushToken?(): string;
  requestNotifications?(): void;
  notificationsAllowed?(): boolean;
  // In-app updates (app 1.5.0+)
  installUpdate?(url: string): void;
  versionCode?(): number;
  // Unlock with face (app 1.6.0+): see lib/passkey.ts
  faceUnlockAvailable?(): boolean;
  phoneKey?(): string;
  savePhoneKey?(json: string): void;
  unlockPhoneKey?(): void;
  clearPhoneKey?(): void;
  // App 1.7.0+: the phone's own unlock signs in; optionally a fingerprint too
  phoneKeyAsks?(): string;
  setPhoneKeyAsks?(on: boolean): void;
}
declare global {
  interface Window {
    VBNative?: NativeBridge;
  }
}
export const nativeBridge = typeof window !== 'undefined' ? window.VBNative : undefined;
export const inAndroidApp = !!nativeBridge?.isScreenShareSupported();
export const isSafari = typeof navigator !== 'undefined' && /^((?!chrome|chromium|crios|fxios|android).)*safari/i.test(navigator.userAgent);
const isAndroidBrowser = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent) && !inAndroidApp;

export const canShareScreen =
  inAndroidApp || (typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function');
export const NO_SHARE_REASON = isAndroidBrowser
  ? "Phone browsers can't share their screen. Install the Video Bridge Android app (Settings → Android app) to share from this phone; you can still watch shares here."
  : "This device's browser can't share its screen (phones and tablets can't). You can still watch others' shares here; share from a computer or the Android app.";

export function loadShareQuality(): ShareQuality {
  try {
    return readPref('shareQuality') === '4k' ? '4k' : 'adaptive';
  } catch {
    return 'adaptive';
  }
}
export function saveShareQuality(q: ShareQuality) {
  savePref('shareQuality', q);
}

/**
 * Share settings that can also be changed while sharing (useLiveKit.tuneShare). 0 = automatic:
 * fps 60 with a hardware encoder or for motion, else 30; height = the whole screen (up to 4K);
 * rate = what the quality and codec call for (12-30 Mbit/s).
 */
export interface ShareTune {
  fps: 0 | 60 | 30 | 15;
  height: 0 | 1440 | 1080 | 720;
  rate: 0 | 40 | 25 | 12 | 6 | 3; // Mbit/s
  sound: boolean;
}
export const DEFAULT_TUNE: ShareTune = { fps: 0, height: 0, rate: 0, sound: true };

// This device only (a laptop and a desktop rarely want the same share), so not a profile pref.
const TUNE_KEY = 'vb.shareTune';
export function loadShareTune(): ShareTune {
  try {
    const t = JSON.parse(localStorage.getItem(TUNE_KEY) ?? '{}') as Partial<ShareTune>;
    return {
      fps: ([0, 60, 30, 15] as const).find((v) => v === t.fps) ?? 0,
      height: ([0, 1440, 1080, 720] as const).find((v) => v === t.height) ?? 0,
      rate: ([0, 40, 25, 12, 6, 3] as const).find((v) => v === t.rate) ?? 0,
      sound: t.sound !== false,
    };
  } catch {
    return DEFAULT_TUNE;
  }
}
export function saveShareTune(t: ShareTune) {
  try {
    localStorage.setItem(TUNE_KEY, JSON.stringify(t));
  } catch {
    /* private mode */
  }
}

export function loadShareMode(): ShareMode {
  return readPref('shareMode') === 'motion' ? 'motion' : 'detail';
}
export function saveShareMode(m: ShareMode) {
  savePref('shareMode', m);
}

/** One line per direction for the 1 Hz room report, e.g. "3840x2160@30 18500kbps H264 limit=none layers=1". */
/**
 * The sharpest screen share this device can send: its own screen in real pixels, capped the way
 * the capture is (3840 on the long side and 2160 on the short side in browsers other than Safari;
 * 3840 on the long side in the Android app). A share is never sharper than the screen it copies.
 */
export function maxShareSize(): { w: number; h: number } | null {
  if (typeof screen === 'undefined' || !canShareScreen) return null;
  const dpr = window.devicePixelRatio || 1;
  const w = Math.round(screen.width * dpr);
  const h = Math.round(screen.height * dpr);
  const long = Math.max(w, h);
  const short = Math.min(w, h);
  const cap = inAndroidApp ? 3840 / long : isSafari ? 1 : Math.min(3840 / long, 2160 / short);
  const k = Math.min(1, cap);
  const even = (v: number) => Math.round((v * k) / 2) * 2;
  return { w: even(w), h: even(h) };
}

